import { ReactNode } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ReviewSparkle } from "components/icons/ReviewSparkle";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { IncomeStatementUploadResult } from "types/Models/estimatedTax";
import { formatDate } from "utils/formatDate";

const AI_EXTRACTED_TOOLTIP_TEXT = "AI-extracted data";

type Props = {
  open: boolean;
  onClose: () => void;
  upload: IncomeStatementUploadResult;
  onReupload: () => void;
};

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="t-flex t-flex-col t-gap-1">
    <span className="t-text-body-sm t-text-text-60">{label}</span>
    <span className="t-text-subtext t-text-text-100">{value}</span>
  </div>
);

export const UploadedStatementDetailModal = ({
  open,
  onClose,
  upload,
  onReupload,
}: Props) => {
  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Portal>
        <Modal.Overlay />
        <Modal.Content size="regular">
          <Modal.Header>
            <span className="t-flex t-items-center t-gap-1">
              <Modal.Title>Uploaded Income Statement</Modal.Title>
              <ConditionalToolTip condition={AI_EXTRACTED_TOOLTIP_TEXT}>
                <span className="t-flex t-items-center">
                  <ReviewSparkle width="16" height="16" />
                </span>
              </ConditionalToolTip>
            </span>
            <Modal.Close onClick={onClose} />
          </Modal.Header>
          <Modal.Body>
            <div className="t-grid t-gap-y-6 t-gap-x-5 t-grid-cols-2">
              <Row label="Tax Year" value={String(upload.tax_year)} />
              <Row
                label="Period"
                value={`${formatDate(upload.period_start_date)} – ${formatDate(
                  upload.period_end_date
                )}`}
              />
              <Row
                label="Total Income"
                value={<AmountSuperScript amount={upload.total_income} />}
              />
              <Row
                label="Total Expenses"
                value={<AmountSuperScript amount={upload.total_expenses} />}
              />
              <Row
                label="Net Income"
                value={<AmountSuperScript amount={upload.net_income} />}
              />
              <Row
                label="Confidence Score"
                value={`${Math.round(upload.confidence_score * 100)}%`}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="t-flex t-items-center t-justify-between">
            <Button type="button" customType="ghost" onClick={onReupload}>
              Re-upload
            </Button>
            <Button type="button" onClick={onClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
};
